import React, { useEffect, useState } from 'react';
import {
  Box, Button, TextField, Typography, Alert, Snackbar, CircularProgress,
  MenuItem, Divider, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { CloudUpload, CheckCircle, ExpandMore, VerifiedUser } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchKycStatus, submitKyc, startDiditVerification } from '../hooks/hooks';

const ID_TYPES = [
  { value: 'national_id', label: 'National ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'driving_licence', label: 'Driving Licence' },
];

const ID_DOCUMENTS = [
  { field: 'kyc_id_front', label: 'ID Front Image', urlField: 'kyc_id_front' },
  { field: 'kyc_id_back', label: 'ID Back Image', urlField: 'kyc_id_back' },
  { field: 'kyc_id_selfie', label: 'Selfie Holding Your ID', urlField: 'kyc_id_selfie' },
];

const BUSINESS_DOCUMENTS = [
  { field: 'brela_certificate', label: 'BRELA Certificate', urlField: 'brela_certificate' },
  { field: 'tin_certificate', label: 'TIN Certificate', urlField: 'tin_certificate' },
  { field: 'ngo_business_license', label: 'Business Licence', urlField: 'ngo_business_license' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#F8FAFC',
    fontSize: 14,
    '& fieldset': { borderColor: '#E2E8F0', borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: '#94A3B8' },
    '&.Mui-focused': {
      backgroundColor: '#fff',
      '& fieldset': { borderColor: '#0BA480', borderWidth: 2 },
    },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0BA480' },
};

const FileUploadField = ({ label, file, existingUrl, error, onChange }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', mb: 0.5 }}>{label}</Typography>
    <Button
      component="label"
      variant="outlined"
      fullWidth
      startIcon={file || existingUrl ? <CheckCircle sx={{ color: '#0BA480' }} /> : <CloudUpload />}
      sx={{
        textTransform: 'none',
        justifyContent: 'flex-start',
        borderRadius: '8px',
        borderColor: error ? '#EF4444' : '#E2E8F0',
        color: '#475569',
        py: 1.2,
        '&:hover': { borderColor: '#0BA480', backgroundColor: 'rgba(11,164,128,0.04)' },
      }}
    >
      {file ? file.name : existingUrl ? 'Replace uploaded file' : 'Upload file'}
      <input type="file" hidden accept="image/*,application/pdf" onChange={onChange} />
    </Button>
    {existingUrl && !file && (
      <Typography sx={{ fontSize: 11, mt: 0.5 }}>
        <a href={existingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0BA480' }}>
          View previously uploaded file
        </a>
      </Typography>
    )}
    {error && (
      <Typography sx={{ fontSize: 11, color: '#EF4444', mt: 0.5 }}>{error}</Typography>
    )}
  </Box>
);

const KycVerification = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userDataString = localStorage.getItem('userData');
  const userData = JSON.parse(userDataString || '{}');
  const isBusiness = ['business', 'super_merchant'].includes(userData?.user?.role);

  const [files, setFiles] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data: kyc, isLoading } = useQuery({
    queryKey: ['kycStatus'],
    queryFn: fetchKycStatus,
    refetchInterval: (query) => (query.state.data?.kyc_status === 'pending' ? 5000 : false),
  });

  const status = kyc?.kyc_status || userData?.user?.kyc_status || 'not_submitted';
  const canEdit = status === 'not_submitted' || status === 'rejected';

  useEffect(() => {
    if (status === 'approved') {
      const stored = JSON.parse(localStorage.getItem('userData') || '{}');
      if (stored.user && stored.user.kyc_status !== 'approved') {
        stored.user.kyc_status = 'approved';
        stored.user.kyc_rejection_reason = null;
        localStorage.setItem('userData', JSON.stringify(stored));
      }
      navigate('/dashboard', { replace: true });
    }
  }, [status, navigate]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id_type: kyc?.id_type || '',
      id_number: kyc?.id_number || '',
      business_name: kyc?.business_name || '',
      business_registration_number: kyc?.business_registration_number || '',
      tax_id: kyc?.tax_id || '',
      address: kyc?.address || '',
    },
    validationSchema: Yup.object({
      id_type: Yup.string().required('ID type is required'),
      id_number: Yup.string().required('ID number is required'),
      ...(isBusiness ? {
        business_name: Yup.string().required('Business name is required'),
        business_registration_number: Yup.string().required('Registration number is required'),
        tax_id: Yup.string().required('Tax ID is required'),
        address: Yup.string().required('Address is required'),
      } : {}),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      Object.entries(files).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });
      mutation.mutate(formData);
    },
  });

  const mutation = useMutation({
    mutationFn: submitKyc,
    onSuccess: (response) => {
      setFieldErrors({});
      const stored = JSON.parse(localStorage.getItem('userData') || '{}');
      if (stored.user) {
        stored.user.kyc_status = response.kyc_status;
        stored.user.kyc_rejection_reason = response.kyc_rejection_reason;
        localStorage.setItem('userData', JSON.stringify(stored));
      }
      queryClient.setQueryData(['kycStatus'], response);
      setSnackbar({ open: true, message: 'KYC submitted successfully. Your documents are now under review.', severity: 'success' });
    },
    onError: (error) => {
      const data = error.response?.data || {};
      setFieldErrors(data);
      const firstError = data.detail || Object.values(data)[0];
      setSnackbar({
        open: true,
        message: Array.isArray(firstError) ? firstError[0] : (firstError || 'Failed to submit KYC. Please try again.'),
        severity: 'error',
      });
    },
  });

  const diditMutation = useMutation({
    mutationFn: startDiditVerification,
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      const data = error.response?.data || {};
      setSnackbar({
        open: true,
        message: data.detail || 'Failed to start verification. Please try again.',
        severity: 'error',
      });
    },
  });

  const handleFileChange = (field) => (e) => {
    const file = e.target.files?.[0];
    if (file) setFiles((prev) => ({ ...prev, [field]: file }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress sx={{ color: '#0BA480' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', display: 'flex', justifyContent: 'center', py: { xs: 4, md: 8 }, px: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 640 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: '#0F172A', mb: 0.5 }}>
          Verify Your Account
        </Typography>
        <Typography sx={{ color: '#64748B', fontSize: 14, mb: 3 }}>
          Complete KYC verification to unlock your ZenoPay dashboard and other services.
        </Typography>

        {status === 'pending' && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: '8px' }}>
            Your identity verification is being processed. This page will update automatically
            once a decision is made.
          </Alert>
        )}

        {status === 'rejected' && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            Your KYC submission was rejected{kyc?.kyc_rejection_reason ? `: ${kyc.kyc_rejection_reason}` : '.'} Please retry verification below.
          </Alert>
        )}

        {status === 'not_submitted' && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: '8px' }}>
            You must complete KYC verification before you can access the dashboard.
          </Alert>
        )}

        {(status === 'not_submitted' || status === 'rejected') && (
          <Box sx={{ bgcolor: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', p: { xs: 2.5, md: 3.5 }, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VerifiedUser sx={{ color: '#0BA480' }} />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0F172A' }}>
                Verify your identity instantly
              </Typography>
            </Box>
            <Typography sx={{ color: '#64748B', fontSize: 14, mb: 2 }}>
              You'll be redirected to complete a quick ID and selfie check, then brought back here automatically.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              disabled={diditMutation.isLoading}
              onClick={() => diditMutation.mutate()}
              sx={{
                py: 1.4,
                fontWeight: 600,
                fontSize: 15,
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #0BA480 0%, #059669 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
              }}
            >
              {diditMutation.isLoading
                ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Starting verification...</>
                : status === 'rejected' ? 'Retry Verification' : 'Start Verification'}
            </Button>
          </Box>
        )}

        {canEdit && (
          <Accordion
            disableGutters
            sx={{
              borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: 'none',
              overflow: 'hidden', '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>
                Prefer to submit documents manually instead?
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
          <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: { xs: 0.5, md: 1 } }}>

            <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#0BA480', mb: 2 }}>
              Identity Information
            </Typography>

            <Box mb={2}>
              <TextField
                select
                fullWidth
                label="ID Type"
                name="id_type"
                value={formik.values.id_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.id_type && Boolean(formik.errors.id_type)}
                helperText={formik.touched.id_type && formik.errors.id_type}
                sx={inputSx}
              >
                {ID_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Box>

            <Box mb={2}>
              <TextField
                fullWidth
                label="ID Number"
                name="id_number"
                value={formik.values.id_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.id_number && Boolean(formik.errors.id_number)}
                helperText={formik.touched.id_number && formik.errors.id_number}
                sx={inputSx}
              />
            </Box>

            {ID_DOCUMENTS.map((doc) => (
              <FileUploadField
                key={doc.field}
                label={doc.label}
                file={files[doc.field]}
                existingUrl={kyc?.[doc.urlField]}
                error={fieldErrors[doc.field]}
                onChange={handleFileChange(doc.field)}
              />
            ))}

            {isBusiness && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#0BA480', mb: 2 }}>
                  Business Information
                </Typography>

                <Box mb={2}>
                  <TextField
                    fullWidth
                    label="Business Name"
                    name="business_name"
                    value={formik.values.business_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.business_name && Boolean(formik.errors.business_name)}
                    helperText={formik.touched.business_name && formik.errors.business_name}
                    sx={inputSx}
                  />
                </Box>

                <Box mb={2}>
                  <TextField
                    fullWidth
                    label="Business Registration Number"
                    name="business_registration_number"
                    value={formik.values.business_registration_number}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.business_registration_number && Boolean(formik.errors.business_registration_number)}
                    helperText={formik.touched.business_registration_number && formik.errors.business_registration_number}
                    sx={inputSx}
                  />
                </Box>

                <Box mb={2}>
                  <TextField
                    fullWidth
                    label="Tax ID (TIN)"
                    name="tax_id"
                    value={formik.values.tax_id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tax_id && Boolean(formik.errors.tax_id)}
                    helperText={formik.touched.tax_id && formik.errors.tax_id}
                    sx={inputSx}
                  />
                </Box>

                <Box mb={2}>
                  <TextField
                    fullWidth
                    label="Business Address"
                    name="address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.address && Boolean(formik.errors.address)}
                    helperText={formik.touched.address && formik.errors.address}
                    sx={inputSx}
                  />
                </Box>

                {BUSINESS_DOCUMENTS.map((doc) => (
                  <FileUploadField
                    key={doc.field}
                    label={doc.label}
                    file={files[doc.field]}
                    existingUrl={kyc?.[doc.urlField]}
                    error={fieldErrors[doc.field]}
                    onChange={handleFileChange(doc.field)}
                  />
                ))}
              </>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={mutation.isLoading}
              sx={{
                mt: 1,
                py: 1.4,
                fontWeight: 600,
                fontSize: 15,
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #0BA480 0%, #059669 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
              }}
            >
              {mutation.isLoading
                ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Submitting...</>
                : status === 'rejected' ? 'Resubmit KYC' : 'Submit KYC'}
            </Button>
          </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KycVerification;
